// S5 보스 분노·광폭 VFX(STAGE-WORLD-PRD 3.3): 분노 = 붉은 불꽃 오라, 광폭 = 보라 불꽃 오라(페이즈 3).
// 발밑 바닥 오라(불꽃 혀가 도는 고리) + 몸 둘레에서 오르는 불씨 + 전환 순간 한 번 번지는 충격파 고리와 불씨 터짐.
// 가산 혼합. 섬광 없음(전환 충격파는 0.8초에 한 번 번지고 사라지는 고리 — 섬광 1초 3회 규칙 안, 맥동은 0.6Hz 사인).
// 화면 흔들림·화면 전체 색 변화 없음. 섬광 줄이기 설정에서는 밝기 60%.
// 드로우콜 +2: 바닥 InstancedMesh 1(오라 1 + 충격파 1) + 불씨 InstancedMesh 1(카메라 정면 점). 풀은 미리 할당하고 CPU로 갱신(프레임 루프 새 객체 없음).
// 예산(품질 particleScale 1 기준): 불씨 ≤ 64개 살아 있음, 초당 22개(광폭 28개), 전환 터짐 1회 = 28개.
import * as THREE from 'three/webgpu';
import { atan, attribute, float, length, sin, smoothstep, uv, vec2 } from 'three/tsl';
import { Ev, type GameEvent } from '../shared/events';
import { BOSS_DEFS, BOSS_KEYS } from '../shared/content/bosses';
import { BossState, type GameView } from '../shared/types';
import { RAGE_EVENT } from '../game/bossRage';

/** 효과별 상한(품질 배율 1). 테스트·문서가 같은 값을 읽는다 */
export const BOSS_RAGE_FX_BUDGET = { ember: 64, perSec: 22, perSecFrenzy: 28, burst: 28, ground: 2 } as const;

/** 분노(1) 붉은 주황, 광폭(2) 보라 — 몸 색(core)과 가장자리 색(edge) */
export const RAGE_COLORS: Readonly<Record<1 | 2, { readonly core: number; readonly edge: number }>> = {
  1: { core: 0xff4a1c, edge: 0xffb040 },
  2: { core: 0xa040ff, edge: 0xff5ae0 },
};

/** 충격파 길이(초)와 최대 반지름 배율(보스 반지름 대비) */
const WAVE_SEC = 0.8, WAVE_R = 5.5;

export class BossRageFx {
  readonly group = new THREE.Group();
  private readonly ground: THREE.InstancedMesh;
  private readonly groundAlpha: THREE.InstancedBufferAttribute;
  private readonly groundKind: THREE.InstancedBufferAttribute;
  private readonly sparks: THREE.InstancedMesh;
  private readonly sparkAlpha: THREE.InstancedBufferAttribute;
  private readonly cap: number;
  private n = 0;
  private readonly px: Float32Array; private readonly py: Float32Array; private readonly pz: Float32Array;
  private readonly vx: Float32Array; private readonly vy: Float32Array; private readonly vz: Float32Array;
  private readonly age: Float32Array; private readonly life: Float32Array; private readonly w0: Float32Array; private readonly w1: Float32Array;
  private readonly cr: Float32Array; private readonly cg: Float32Array; private readonly cb: Float32Array; private readonly a0: Float32Array;
  private seed = 0x51ed27f1;
  private acc = 0;
  private reduceFlash = false;
  /** 충격파: 남은 시간(초, 0 = 없음)·자리·종류·보스 반지름 */
  private waveAge = -1; private waveX = 0; private waveZ = 0; private waveKind: 1 | 2 = 1; private waveR = 2;
  /** 오라 세기(0..1, 켜질 때 0.5초에 걸쳐 오르고 보스가 쓰러지면 내려간다) */
  private level = 0;
  private readonly right = new THREE.Vector3();
  private readonly up = new THREE.Vector3();
  private readonly fwd = new THREE.Vector3();
  /** QA: 누적 생성 수·이번 프레임 오라를 그렸는지 */
  readonly spawned = { ember: 0, burst: 0 };
  auraOn = false;

  constructor(particleScale: number) {
    const scale = Math.max(0.25, particleScale);
    this.cap = Math.max(16, Math.floor(BOSS_RAGE_FX_BUDGET.ember * scale));
    // 바닥: aKind 0 = 오라(가운데 옅고 불꽃 혀 12개가 돈 고리), 1 = 충격파(얇은 고리)
    const gg = new THREE.PlaneGeometry(1, 1);
    this.groundAlpha = new THREE.InstancedBufferAttribute(new Float32Array(BOSS_RAGE_FX_BUDGET.ground), 1);
    this.groundAlpha.setUsage(THREE.DynamicDrawUsage);
    this.groundKind = new THREE.InstancedBufferAttribute(new Float32Array(BOSS_RAGE_FX_BUDGET.ground * 2), 2);
    this.groundKind.setUsage(THREE.DynamicDrawUsage);
    gg.setAttribute('aAlpha', this.groundAlpha);
    gg.setAttribute('aKind', this.groundKind);
    const gm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
    gm.forceSinglePass = true; gm.fog = false;
    const p = uv().sub(0.5).mul(2);
    const d = length(vec2(p.x, p.y));
    const ang = atan(p.y, p.x);
    const k = attribute('aKind', 'vec2');
    // 불꽃 혀: 각도 12갈래 사인이 시간(aKind.y = 위상)에 따라 돈다. 고리 두께가 혀 따라 출렁인다
    const tongue = sin(ang.mul(12).add(k.y)).mul(0.5).add(0.5);
    const tongue2 = sin(ang.mul(7).sub(k.y.mul(1.7))).mul(0.5).add(0.5);
    const flame = tongue.mul(tongue2).pow(1.5);
    const auraRing = smoothstep(float(0.2), float(0.0), d.sub(float(0.74).add(flame.mul(0.12))).abs()).mul(float(0.55).add(flame.mul(0.65)));
    const auraFill = smoothstep(float(0.95), float(0.0), d).pow(2.2).mul(0.32);
    const aura = auraRing.add(auraFill).mul(smoothstep(float(1.0), float(0.9), d));
    const wave = smoothstep(float(0.09), float(0.0), d.sub(0.9).abs()).add(smoothstep(float(0.9), float(0.6), d).mul(0.12)).mul(smoothstep(float(1.0), float(0.95), d));
    gm.opacityNode = aura.mul(float(1).sub(k.x)).add(wave.mul(k.x)).mul(attribute('aAlpha', 'float'));
    this.ground = new THREE.InstancedMesh(gg, gm, BOSS_RAGE_FX_BUDGET.ground);
    this.ground.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.ground.setColorAt(0, new THREE.Color(1, 1, 1));
    (this.ground.instanceColor as THREE.InstancedBufferAttribute).setUsage(THREE.DynamicDrawUsage);
    this.ground.frustumCulled = false; this.ground.count = 0; this.ground.visible = false; this.ground.renderOrder = 17;
    // 불씨: 가산 점(카메라 정면)
    const sg = new THREE.PlaneGeometry(1, 1);
    this.sparkAlpha = new THREE.InstancedBufferAttribute(new Float32Array(this.cap), 1);
    this.sparkAlpha.setUsage(THREE.DynamicDrawUsage);
    sg.setAttribute('aAlpha', this.sparkAlpha);
    const sm = new THREE.MeshBasicNodeMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide, blending: THREE.AdditiveBlending });
    sm.forceSinglePass = true; sm.fog = false;
    const q = uv().sub(0.5).mul(2);
    // 불씨는 위로 긴 물방울 모양(오르는 불꽃처럼)
    const sd = length(vec2(q.x.mul(1.35), q.y.mul(0.8)));
    sm.opacityNode = smoothstep(float(1), float(0.0), sd).pow(1.7).mul(attribute('aAlpha', 'float'));
    this.sparks = new THREE.InstancedMesh(sg, sm, this.cap);
    this.sparks.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    this.sparks.setColorAt(0, new THREE.Color(1, 1, 1));
    (this.sparks.instanceColor as THREE.InstancedBufferAttribute).setUsage(THREE.DynamicDrawUsage);
    this.sparks.frustumCulled = false; this.sparks.count = 0; this.sparks.visible = false; this.sparks.renderOrder = 22;
    this.group.add(this.ground, this.sparks);
    const f = (): Float32Array => new Float32Array(this.cap);
    this.px = f(); this.py = f(); this.pz = f(); this.vx = f(); this.vy = f(); this.vz = f();
    this.age = f(); this.life = f(); this.w0 = f(); this.w1 = f();
    this.cr = f(); this.cg = f(); this.cb = f(); this.a0 = f();
  }

  private rnd(): number {
    this.seed = (Math.imul(this.seed, 1664525) + 1013904223) >>> 0;
    return this.seed / 4294967296;
  }

  setReduceFlash(on: boolean): void { this.reduceFlash = on; }
  get active(): number { return this.n; }

  private spawn(x: number, y: number, z: number, vx: number, vy: number, vz: number, life: number, w0: number, w1: number, color: number, alpha: number): void {
    if (this.n >= this.cap) return;
    const i = this.n++;
    this.px[i] = x; this.py[i] = y; this.pz[i] = z; this.vx[i] = vx; this.vy[i] = vy; this.vz[i] = vz;
    this.age[i] = 0; this.life[i] = life; this.w0[i] = w0; this.w1[i] = w1;
    this.cr[i] = (color >> 16 & 255) / 255; this.cg[i] = (color >> 8 & 255) / 255; this.cb[i] = (color & 255) / 255;
    this.a0[i] = alpha * (this.reduceFlash ? 0.6 : 1);
  }

  private kill(i: number): void {
    const j = --this.n;
    if (i === j) return;
    this.px[i] = this.px[j]; this.py[i] = this.py[j]; this.pz[i] = this.pz[j]; this.vx[i] = this.vx[j]; this.vy[i] = this.vy[j]; this.vz[i] = this.vz[j];
    this.age[i] = this.age[j]; this.life[i] = this.life[j]; this.w0[i] = this.w0[j]; this.w1[i] = this.w1[j];
    this.cr[i] = this.cr[j]; this.cg[i] = this.cg[j]; this.cb[i] = this.cb[j]; this.a0[i] = this.a0[j];
  }

  /** 바닥 판 하나(오라 k 0 / 충격파 k 1)를 다음 인스턴스에 쓴다 */
  private gn = 0;
  private put(x: number, z: number, w: number, color: number, alpha: number, k: number, phase: number): void {
    const gm = this.ground.instanceMatrix.array as Float32Array, gc = this.ground.instanceColor!.array as Float32Array;
    const ga = this.groundAlpha.array as Float32Array, gk = this.groundKind.array as Float32Array, n = this.gn++;
    const o = n * 16;
    gm[o] = w; gm[o + 1] = 0; gm[o + 2] = 0; gm[o + 3] = 0;
    gm[o + 4] = 0; gm[o + 5] = 0; gm[o + 6] = -w; gm[o + 7] = 0;
    gm[o + 8] = 0; gm[o + 9] = 1; gm[o + 10] = 0; gm[o + 11] = 0;
    gm[o + 12] = x; gm[o + 13] = 0.05 + n * 0.01; gm[o + 14] = z; gm[o + 15] = 1;
    gc[n * 3] = (color >> 16 & 255) / 255; gc[n * 3 + 1] = (color >> 8 & 255) / 255; gc[n * 3 + 2] = (color & 255) / 255;
    ga[n] = alpha; gk[n * 2] = k; gk[n * 2 + 1] = phase;
  }

  /** 분노·광폭 진입(Ev.BossPattern a 10·11): 충격파 고리 + 불씨 터짐 */
  onEvent(e: GameEvent): void {
    if (e.type === Ev.SceneChange) { this.clear(); return; }
    if (e.type !== Ev.BossPattern || (e.a !== RAGE_EVENT.rage && e.a !== RAGE_EVENT.frenzy)) return;
    const kind: 1 | 2 = e.a === RAGE_EVENT.rage ? 1 : 2, col = RAGE_COLORS[kind], def = BOSS_DEFS[BOSS_KEYS[e.c | 0] ?? 'treant'];
    const r = def.radius, h = 6.2 * def.scale;
    this.waveAge = 0; this.waveX = e.x; this.waveZ = e.z; this.waveKind = kind; this.waveR = r;
    for (let i = 0; i < BOSS_RAGE_FX_BUDGET.burst; i++) {
      const a = (i / BOSS_RAGE_FX_BUDGET.burst) * Math.PI * 2 + this.rnd() * 0.2, s = 3.2 + this.rnd() * 2.6;
      this.spawn(e.x + Math.sin(a) * r * 0.8, 0.3 + this.rnd() * h * 0.5, e.z + Math.cos(a) * r * 0.8, Math.sin(a) * s, 1.4 + this.rnd() * 2.2, Math.cos(a) * s,
        0.7 + this.rnd() * 0.4, 0.55, 0.12, this.rnd() < 0.6 ? col.core : col.edge, 0.95);
    }
    this.spawned.burst++;
  }

  /** 매 프레임. dt = 세계 시계 초(일시정지·히트스톱 0) */
  update(view: GameView, dt: number, camera: THREE.Camera, time: number): void {
    const b = view.boss, rage = (b.rage ?? 0) | 0;
    const alive = b.state !== BossState.Inactive && b.state !== BossState.Dead;
    const on = rage > 0 && alive && b.state !== BossState.Dying;
    // 오라 세기: 켜지면 0.5초에 걸쳐 오르고, 쓰러지면 0.4초에 걸쳐 내려간다(갑자기 번쩍이지 않게)
    if (dt > 0) this.level = on ? Math.min(1, this.level + dt * 2) : Math.max(0, this.level - dt * 2.5);
    if (!alive && this.level <= 0) this.level = 0;
    camera.matrixWorld.extractBasis(this.right, this.up, this.fwd);
    this.gn = 0;
    const kind: 1 | 2 = rage === 2 ? 2 : 1, col = RAGE_COLORS[kind], def = BOSS_DEFS[BOSS_KEYS[b.variant] ?? 'treant'];
    const r = def.radius, h = 6.2 * def.scale;
    const dim = this.reduceFlash ? 0.6 : 1;
    this.auraOn = this.level > 0 && alive;
    if (this.auraOn) {
      const pulse = 0.85 + 0.15 * Math.sin(time * Math.PI * 1.2);
      this.put(b.x, b.z, r * 2 * 2.3, col.core, 0.85 * this.level * pulse * dim, 0, time * (kind === 2 ? 2.6 : 1.8));
    }
    if (this.waveAge >= 0) {
      if (dt > 0) this.waveAge += dt;
      const t = this.waveAge / WAVE_SEC;
      if (t >= 1) this.waveAge = -1;
      else {
        const ease = 1 - (1 - t) * (1 - t);
        this.put(this.waveX, this.waveZ, this.waveR * 2 * (1 + (WAVE_R - 1) * ease), RAGE_COLORS[this.waveKind].edge, (1 - t) * (1 - t) * 0.95 * dim, 1, 0);
      }
    }
    const gn = this.gn;
    this.ground.count = gn; this.ground.visible = gn > 0;
    this.ground.instanceMatrix.needsUpdate = true; this.ground.instanceColor!.needsUpdate = true; this.groundAlpha.needsUpdate = true; this.groundKind.needsUpdate = true;
    // 몸 둘레에서 오르는 불씨(광폭은 조금 더 많이, 빙글 돌며)
    if (dt > 0 && this.auraOn && this.level > 0.3) {
      this.acc = Math.min(4, this.acc + dt * (kind === 2 ? BOSS_RAGE_FX_BUDGET.perSecFrenzy : BOSS_RAGE_FX_BUDGET.perSec));
      while (this.acc >= 1) {
        this.acc -= 1;
        const a = this.rnd() * Math.PI * 2, rr = r * (0.7 + this.rnd() * 0.6), sw = kind === 2 ? 1.2 : 0.3;
        this.spawn(b.x + Math.sin(a) * rr, 0.2 + this.rnd() * h * 0.35, b.z + Math.cos(a) * rr, Math.cos(a) * sw, 1.6 + this.rnd() * 1.4, -Math.sin(a) * sw,
          0.9 + this.rnd() * 0.5, 0.42 + this.rnd() * 0.2, 0.1, this.rnd() < 0.55 ? col.core : col.edge, 0.85);
        this.spawned.ember++;
      }
    }
    const R = this.right, U = this.up, F = this.fwd;
    const sm = this.sparks.instanceMatrix.array as Float32Array, sc = this.sparks.instanceColor!.array as Float32Array, sa = this.sparkAlpha.array as Float32Array;
    let i = 0;
    while (i < this.n) {
      if (dt > 0) {
        this.age[i] += dt;
        if (this.age[i] >= this.life[i]) { this.kill(i); continue; }
        const k = Math.max(0, 1 - 1.4 * dt);
        this.vx[i] *= k; this.vz[i] *= k; this.vy[i] = this.vy[i] * Math.max(0, 1 - 0.6 * dt) + 0.4 * dt;
        this.px[i] += this.vx[i] * dt; this.py[i] += this.vy[i] * dt; this.pz[i] += this.vz[i] * dt;
      }
      const t = this.age[i] / this.life[i];
      const w = this.w0[i] + (this.w1[i] - this.w0[i]) * t, hgt = w * 1.6;
      const a = this.a0[i] * Math.min(1, t * 6) * (1 - t * t);
      const o = i * 16;
      sm[o] = R.x * w; sm[o + 1] = R.y * w; sm[o + 2] = R.z * w; sm[o + 3] = 0;
      sm[o + 4] = U.x * hgt; sm[o + 5] = U.y * hgt; sm[o + 6] = U.z * hgt; sm[o + 7] = 0;
      sm[o + 8] = F.x; sm[o + 9] = F.y; sm[o + 10] = F.z; sm[o + 11] = 0;
      sm[o + 12] = this.px[i]; sm[o + 13] = this.py[i]; sm[o + 14] = this.pz[i]; sm[o + 15] = 1;
      sc[i * 3] = this.cr[i]; sc[i * 3 + 1] = this.cg[i]; sc[i * 3 + 2] = this.cb[i];
      sa[i] = a;
      i++;
    }
    this.sparks.count = this.n; this.sparks.visible = this.n > 0;
    this.sparks.instanceMatrix.needsUpdate = true; this.sparks.instanceColor!.needsUpdate = true; this.sparkAlpha.needsUpdate = true;
  }

  /** 셰이더 미리 컴파일용 */
  prewarm(on: boolean): void {
    this.ground.count = on ? 1 : 0; this.ground.visible = on;
    this.sparks.count = on ? 1 : 0; this.sparks.visible = on;
  }

  clear(): void {
    this.n = 0; this.acc = 0; this.waveAge = -1; this.level = 0; this.auraOn = false;
    this.ground.count = 0; this.ground.visible = false; this.sparks.count = 0; this.sparks.visible = false;
  }

  dispose(): void {
    for (const mesh of [this.ground, this.sparks]) { mesh.geometry.dispose(); (mesh.material as THREE.Material).dispose(); mesh.dispose(); }
  }
}
